import {Button, Paper} from '@material-ui/core';
import {Alert, AlertTitle} from '@material-ui/lab';
import {Async, Loaded} from 'Core/models/Async';
import {Email, eqEmail, ValidEmail} from 'Core/models/Email';
import {Either, fold, isRight, left, right} from 'fp-ts/lib/Either';
import {identity} from 'fp-ts/lib/function';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import EmailInput from 'Shared/components/EmailInput';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {removeUnsupportedSpecialChars} from 'Shared/utilities/regexUtility';
import {saveEmail} from 'Users/ducks/userAccount';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {changeAnalyticsState} from 'Core/ducks/analytics';

export type UserEmailSectionProps = {
  userId: string;
  asyncEmail: Either<Email, Email>;
  onChange: (email: Async<Either<Email, Email>>) => void;
  saveEmail: typeof saveEmail;
} & WithSelectedAgencyProps;

const emailWithSupportedChars = (email: Email) => ValidEmail.create(removeUnsupportedSpecialChars(email.address));

const UserEmailSection = (props: UserEmailSectionProps) => {
  const dispatch = useDispatch();
  const {selectedAgency} = props;
  const email = pipe(props.asyncEmail, fold(identity, identity));
  const [originalEmail, setOriginalEmail] = useState(email);
  const isEditing = isRight(props.asyncEmail);
  const hasEmailChanged = !eqEmail.equals(originalEmail, email);
  const {analytics} = useSelector((state: ReducerState) => state.analytics);

  return (
    <Paper className="user-profile__user-info-section">
      <div className="user-info-section__header">
        <h3>Email</h3>
      </div>
      {isEditing ? (
        <Alert severity="info" style={{marginBottom: '1.5rem', fontSize: '.95rem'}}>
          <AlertTitle>The following special characters are allowed: </AlertTitle>
          <strong>Underscore, Dash, Period and the At Symbol ( _ - . @ )</strong>
        </Alert>
      ) : (
        <></>
      )}
      <div className="user-info-section__name-fields">
        <EmailInput
          className="user-info-section__profile-input"
          label="Email"
          disabled={!isEditing}
          onChange={newEmail => {
            return props.onChange(new Loaded(right(emailWithSupportedChars(newEmail))));
          }}
          email={email}
        />
      </div>
      <div className="user-info-section__actions-container">
        {isEditing ? (
          <>
            <Button
              variant="contained"
              className="user-info-section__cancel-user-information-button"
              onClick={() => {
                props.onChange(new Loaded(left(originalEmail)));
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!hasEmailChanged || !(email instanceof ValidEmail)}
              color="primary"
              variant="contained"
              className="user-info-section__save-user-information-button"
              onClick={() =>
                props.saveEmail(
                  dispatch,
                  props.userId,
                  email as ValidEmail,
                  originalEmail as ValidEmail,
                  selectedAgency?.encryptedIdentifier,
                  selectedAgency?.maskedIdentifier
                )
              }
            >
              Save
            </Button>
          </>
        ) : (
          <Button
            className="user-info-section__edit-button"
            aria-label="Email Information Edit"
            color="primary"
            variant="contained"
            onClick={() => {
              dispatch(
                changeAnalyticsState({
                  page: analytics.page,
                  ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.USER_PROFILE_EDITEMAIL},
                })
              );
              props.onChange(new Loaded(right(email)));
            }}
          >
            Edit&nbsp;&nbsp;
            <i className="fa fa-edit" />
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default withSelectedAgency(UserEmailSection);
